import React, { useState, useEffect, useRef } from "react"
import { Card, CardBody, Row, Col, Button, Spinner } from "reactstrap"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import seoApi from "Api/seoApi"
import { InputText } from "primereact/inputtext"
import { InputTextarea } from "primereact/inputtextarea"
import { Toast } from "primereact/toast"
import "./EditSeo.scss"

const EditSeo = () => {
  document.title = "Edit SEO"
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useRef(null)
  const [loading, setLoading] = useState(false)
  const [seoDetails, setSeoDetails] = useState({
    pageName: "",
    title: "",
    description: "",
    keywords: "",
    schema: ""
  })
  const accessToken = localStorage.getItem("accessToken")

  const breadcrumbItems = [
    { label: "Pixwik", link: "#" },
    { label: "SEO", link: "/seo" },
    { label: "Edit SEO", link: "#" },
  ]

  useEffect(() => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    seoApi
      .getSeoById(id, config)
      .then(response => {
        if (response.data.success) {
          const data = response.data.data
          setSeoDetails({
            ...data,
            schema: JSON.stringify(data.schema, null, 2)
          })
        }
      })
      .catch(error => {
        console.log("Error fetching SEO details:", error)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch SEO details",
          life: 3000,
        })
      })
  }, [id, accessToken])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setSeoDetails(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSchemaChange = (e) => {
    const { value } = e.target
    setSeoDetails(prev => ({
      ...prev,
      schema: value
    }))
  }

  const handleSubmit = async () => {
    try {
      setLoading(true)
      
      // Validate and parse schema
      let parsedSchema
      try {
        parsedSchema = JSON.parse(seoDetails.schema)
      } catch (error) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Invalid JSON schema format",
          life: 3000,
        })
        setLoading(false)
        return
      }

      const payload = {
        ...seoDetails,
        schema: parsedSchema
      }

      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }

      const response = await seoApi.updateSeo(id, payload, config)
      
      if (response.data.success) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "SEO updated successfully",
          life: 3000,
        })
        setTimeout(() => {
          navigate("/seo")
        }, 1000)
      }
    } catch (error) {
      console.error("Error updating SEO:", error)
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to update SEO",
        life: 3000,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="edit-seo">
      <Toast ref={toast} />
      <Row>
        <Col xs="12">
          <div className="seo-list-header d-flex align-items-center justify-content-between">
            <CustomBreadCrumb data={breadcrumbItems} pageTitle="Edit SEO" />
            <Button
              color="secondary"
              className="waves-effect"
              onClick={() => navigate("/seo")}
            >
              <i className="ion ion-md-arrow-back me-2"></i>
              Back
            </Button>
          </div>

          <Card className="mt-4">
            <CardBody>
              <h5 className="border-bottom pb-2 mb-4">Edit SEO Details</h5>
              
              <div className="seo-edit-form">
                <div className="seo-edit-inp">
                  <label htmlFor="pageName" className="label">
                    Page Name
                  </label>
                  <InputText
                    id="pageName"
                    name="pageName"
                    value={seoDetails.pageName}
                    onChange={handleInputChange}
                    className="inp-field-box"
                    placeholder="Enter page name"
                  />
                </div>

                <div className="seo-edit-inp">
                  <label htmlFor="title" className="label">
                    Title
                  </label>
                  <InputText
                    id="title"
                    name="title"
                    value={seoDetails.title}
                    onChange={handleInputChange}
                    className="inp-field-box"
                    placeholder="Enter title"
                  />
                </div>

                <div className="seo-edit-inp">
                  <label htmlFor="description" className="label">
                    Description
                  </label>
                  <InputTextarea
                    id="description"
                    name="description"
                    value={seoDetails.description}
                    onChange={handleInputChange}
                    className="inp-field-box"
                    rows={3}
                    placeholder="Enter description"
                  />
                </div>

                <div className="seo-edit-inp">
                  <label htmlFor="keywords" className="label">
                    Keywords
                  </label>
                  <InputTextarea
                    id="keywords"
                    name="keywords"
                    value={seoDetails.keywords}
                    onChange={handleInputChange}
                    className="inp-field-box"
                    rows={2}
                    placeholder="Enter keywords"
                  />
                </div>

                <div className="seo-edit-inp">
                  <label htmlFor="schema" className="label">
                    Schema
                  </label>
                  <InputTextarea
                    id="schema"
                    name="schema"
                    value={seoDetails.schema}
                    onChange={handleSchemaChange}
                    className="schema-input"
                    rows={10}
                    placeholder="Enter JSON schema"
                  />
                </div>

                <div className="d-flex justify-content-end mt-4">
                  <Button
                    type="button"
                    color="secondary"
                    className="me-2"
                    onClick={() => navigate("/seo")}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Updating...
                      </>
                    ) : (
                      "Update"
                    )}
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default EditSeo 